import React, { useState } from 'react';
import { useTenant } from '../../context/TenantContext';

/**
 * Environment Banner
 * ──────────────────
 * Sticky banner shown on non-production environments to make clear to
 * testers/investors that they are looking at UAT or preview data, not
 * a live customer environment.
 *
 * Visibility rules:
 *   environment === 'production'  → hidden
 *   environment === 'uat'         → orange banner: "UAT Environment"
 *   environment === 'preview'     → blue banner: "Preview Build"
 *   environment === 'local'       → grey banner: "Local Dev"
 *
 * The banner is dismissible per-session; clicking the X stores a flag in
 * sessionStorage so the user is not constantly nagged on every navigation.
 */
const EnvironmentBanner = () => {
  const { environment, branding, tenantLocked, tenantSource } = useTenant();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('clearequal.envBannerDismissed') === '1';
    } catch {
      return false;
    }
  });

  if (environment === 'production' || dismissed) return null;

  const labels = {
    uat: { text: 'UAT, Testing Environment', bg: '#F97316', fg: '#FFFFFF' },
    preview: { text: 'Preview Build', bg: '#0EA5E9', fg: '#FFFFFF' },
    local: { text: 'Local Development', bg: '#64748B', fg: '#FFFFFF' },
  };
  const cfg = labels[environment] || labels.local;

  // Build version string from Vite's injected env vars (set in vite.config.mjs)
  const version = (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev');
  const buildTime = (typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev');

  const handleDismiss = () => {
    try { sessionStorage.setItem('clearequal.envBannerDismissed', '1'); } catch (_) {}
    setDismissed(true);
  };

  return (
    <div
      style={{
        background: cfg.bg,
        color: cfg.fg,
        fontSize: '12px',
        fontWeight: 600,
        padding: '6px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 60,
        letterSpacing: '0.02em',
      }}
      role="status"
      aria-label="Environment indicator"
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{cfg.text}</span>
        <span style={{ opacity: 0.85 }}>v{version}</span>
        {buildTime !== 'dev' && (
          <span style={{ opacity: 0.7, fontWeight: 400 }}>
            built {new Date(buildTime).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
        {tenantLocked && (
          <span style={{ opacity: 0.85 }}>
            · tenant: {branding.displayName} (subdomain-locked)
          </span>
        )}
        {!tenantLocked && tenantSource !== 'default' && (
          <span style={{ opacity: 0.7, fontWeight: 400 }}>
            · tenant via {tenantSource}
          </span>
        )}
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss environment banner"
        style={{
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          color: cfg.fg,
          width: 24,
          height: 24,
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
};

export default EnvironmentBanner;
